/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

/**
 * Mutation hook for manually refreshing the current price of an equity security.
 * Calls the `stock-price` edge function and updates the equity_securities table.
 *
 * Requirements: 7.2, 7.3, 7.4, 7.5
 */
export function useRefreshStockPrice() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      symbol,
      exchange,
      equityId,
    }: {
      symbol: string;
      exchange: string;
      equityId: string;
    }) => {
      const { data, error } = await supabase.functions.invoke('stock-price', {
        body: { symbol, exchange },
      });

      if (error) throw error;
      if (!data?.price) throw new Error('Invalid price data');

      const { error: updateError } = await supabase
        .from('equity_securities' as any)
        .update({ current_price: data.price, updated_at: new Date().toISOString() })
        .eq('id', equityId);

      if (updateError) throw updateError;

      return data;
    },

    onSuccess: (_result, { equityId }) => {
      qc.invalidateQueries({ queryKey: ['equity-securities'] });
      qc.invalidateQueries({ queryKey: ['equity-security-detail', equityId] });
      toast.success('Price refreshed successfully.');
    },

    onError: () => {
      toast.error('Failed to refresh price. Please try again.');
    },
  });
}
